import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UnmaskedData {
  userId: string | number;
  data: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    nhsNumber?: string;
    [key: string]: string | number | undefined;
  };
}

@Injectable({
  providedIn: 'root'
})
export class UnmaskingStateService {
  private unmaskedState = new BehaviorSubject<UnmaskedData[]>([]);
  private timeoutHandles: Map<string | number, any> = new Map();
  private readonly AUTO_HIDE_TIME = 120000;

  getUnmaskedState(): Observable<UnmaskedData[]> {
    return this.unmaskedState.asObservable();
  }

  updateState(userId: string | number, data: UnmaskedData['data']) {
    const currentState = [...this.unmaskedState.value];
    const existingIndex = currentState.findIndex(item => String(item.userId) === String(userId));

    if (existingIndex > -1) {
      currentState[existingIndex] = { ...currentState[existingIndex], data: { ...currentState[existingIndex].data, ...data } };
    } else {
      currentState.push({ userId, data });
    }

    this.unmaskedState.next(currentState);
    this.resetTimer(userId);
  }

  resetTimer(userId: string | number) {
    if (this.timeoutHandles.has(userId)) {
      clearTimeout(this.timeoutHandles.get(userId));
    }

    const handle = setTimeout(() => {
      this.clearState(userId);
    }, this.AUTO_HIDE_TIME);

    this.timeoutHandles.set(userId, handle);
  }

  clearState(userId: string | number) {
    const newState = this.unmaskedState.value.filter(item => String(item.userId) !== String(userId));
    this.unmaskedState.next(newState);

    if (this.timeoutHandles.has(userId)) {
      clearTimeout(this.timeoutHandles.get(userId));
      this.timeoutHandles.delete(userId);
    }
  }

  getUnmaskedValue(userId: string | number, field: string): string | undefined {
    const userState = this.unmaskedState.value.find(item => String(item.userId) === String(userId));
    return userState?.data[field] as string;
  }

  isUserUnmasked(userId: string | number): boolean {
    return this.unmaskedState.value.some(item => String(item.userId) === String(userId));
  }
}
